import { apiClient } from '@core/api/ApiClientFactory.ts';
import type { DeviceModule, Module, Gateway } from '@core/types';
import type { LogicConfiguration } from './types.ts';

export const moduleService = {
    async createModule(module: DeviceModule): Promise<DeviceModule> {
        try {
            const response = await apiClient.post<DeviceModule>(module.kind, module);
            return response.data;
        } catch (error) {
            console.error('[ModuleService] Error creating module:', error);
            throw error;
        }
    },

    async updateModule(kind: string, id: string, data: Partial<Module>): Promise<Module> {
        try {
            const response = await apiClient.put<Module>(`${kind}/${id}`, data);
            return response.data;
        } catch (error) {
            console.error(`[ModuleService] Error updating module ${kind}/${id}:`, error);
            throw error;
        }
    },

    /**
     * Update a gateway specifically
     * @param deviceId - The device ID of the gateway
     * @param data - The gateway data to update
     */
    async updateGateway(deviceId: string, data: Partial<Gateway>): Promise<Gateway> {
        try {
            const response = await apiClient.put<Gateway>(`gateway/${deviceId}`, data);
            return response.data;
        } catch (error) {
            console.error(`[ModuleService] Error updating gateway ${deviceId}:`, error);
            throw error;
        }
    },

    async deleteModule(kind: string, id: string): Promise<void> {
        try {
            await apiClient.delete<void>(`${kind}/${id}`);
            return;
        } catch (error) {
            console.error('[ModuleService] Error deleting module:', error);
            throw error;
        }
    },

    async sendModuleToRecycle(kind: string, id: string): Promise<void> {
        try {
            await apiClient.post<void>(`${kind}/${id}/send-to-recycle-bin`);
            return;
        } catch (error) {
            console.error(`[ModuleService] Error sending module to recycle ${kind}/${id}:`, error);
            throw error;
        }
    },

    async postModuleAction(kind: string, id: string, actionUri: string, data?: DeviceModule): Promise<void> {
        try {
            await apiClient.post<void>(`${kind}/${id}${actionUri}`, data || {});
            return;
        } catch (error) {
            console.error('[ModuleService] Error posting module action:', error);
            throw error;
        }
    },

    // Module-specific helpers
    async loadShelterFillStatistics(
        deviceId: string,
        moduleId: string
    ): Promise<{
        currentCount: number;
        capacity?: number;
        [key: string]: any;
    }> {
        try {
            const response = await apiClient.get<{
                currentCount: number;
                capacity?: number;
            }>(`statistics/shelterfilllevel/${deviceId}/${moduleId}`);
            return response.data;
        } catch (error) {
            console.error('[ModuleService] Error loading shelter fill statistics:', error);
            throw error;
        }
    },

    async loadTrolleyShelterLocationImage(imagePathUri: string): Promise<ArrayBuffer> {
        try {
            const response = await apiClient.get(
                `trolleyshelterlocationimage?imagePathUri=${encodeURIComponent(imagePathUri)}`,
                {
                    responseType: 'arraybuffer'
                }
            );
            return response.data;
        } catch (error) {
            console.error('[ModuleService] Error loading trolley shelter location image:', error);
            throw error;
        }
    },

    async createTrolleyShelterLocationImage(formData: FormData): Promise<string> {
        try {
            const response = await apiClient.post('trolleyshelterlocationimage', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            return response.data;
        } catch (error) {
            console.error('[ModuleService] Error creating trolley shelter location image:', error);
            throw error;
        }
    },

    async deleteTrolleyShelterLocationImage(imagePathUri: string): Promise<void> {
        try {
            await apiClient.delete(`trolleyshelterlocationimage?imagePathUri=${encodeURIComponent(imagePathUri)}`);
            return;
        } catch (error) {
            console.error('[ModuleService] Error deleting trolley shelter location image:', error);
            throw error;
        }
    },

    // Recycle Bin Methods
    async loadAllRecycledModules(): Promise<Module[]> {
        try {
            const response = await apiClient.get<Module[]>('shop/getAllWithRecycled');
            return response.data;
        } catch (error) {
            console.error('[ModuleService] Error loading all recycled modules:', error);
            throw error;
        }
    },

    async restoreModuleFromRecycleBin(kind: string, id: string): Promise<void> {
        try {
            await apiClient.post<void>(`${kind}/${id}/restore-from-recycle-bin`);
            return;
        } catch (error) {
            console.error(`[ModuleService] Error restoring module from recycle bin ${kind}/${id}:`, error);
            throw error;
        }
    }
};
